import {
  createTheme,
  type PaletteColor,
  type PaletteColorOptions,
  type SimplePaletteColorOptions,
  type ThemeOptions
} from "@mui/material/styles";
import { createStyled } from "@mui/system";

import {
  autostoreDesignSystemColors,
  type AutostoreDesignSystemColors
} from "./autostoreDesignSystem";
import { basicColours, mainColours } from "./colours";
import { muiAlert } from "./muiAlert";
import { muiAlertTitle } from "./muiAlertTitle";
import { muiAppBar } from "./muiAppBar";
import { muiButton } from "./muiButton";
import { muiButtonGroup } from "./muiButtonGroup";
import { muiCard } from "./muiCard";
import { muiChip } from "./muiChip";
import { muiDialogTitle } from "./muiDialogTitle";
import { muiFormControl } from "./muiFormControl";
import { muiFormLabel } from "./muiFormLabel";
import { muiInputBase } from "./muiInputBase";
import { muiList } from "./muiList";
import { muiMenuItem } from "./muiMenuItem";
import { muiOutlinedInput } from "./muiOutlinedInput";
import { muiPaper } from "./muiPaper";
import { muiSvgIcon } from "./muiSvgIcon";
import { muiSwitch } from "./muiSwitch";
import { muiTable } from "./muiTable";
import { muiTableCell } from "./muiTableCell";
import { muiTableHead } from "./muiTableHead";
import { muiTableRow } from "./muiTableRow";
import { muiToolbar } from "./muiToolbar";

declare module "@mui/material/styles" {
  interface TypeBackground {
    hover: string;
    lightBlue: string;
    gray: string;
    secondary: string;
  }
  interface TypographyVariants {
    tableBody: React.CSSProperties;
    stat: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    tableBody?: React.CSSProperties;
    stat: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    tableBody: true;
    stat: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    outlined: false; // Exclude the outlined variant
    serious: true;
    subtle: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    "1": true;
    "2": true;
    "3": true;
    "4": true;
    "5": true;
    "6": true;
    "7": true;
    "8": true;
    "9": true;
    "10": true;
    "11": true;
    "12": true;
    ambient: true;
    chilled: true;
    frozen: true;
    // disable colors in mui that are not in the design system
    primary: false;
    default: false;
  }
}

/**
 * There are a few ways to reference theme colors:
 *
 * Using the sx prop on MUI elements provides access to the theme (colors, spacing, etc)
 * @example
 * <Box
 *     sx={{
 *       width: 300,
 *       height: 300,
 *       backgroundColor: 'primary.dark',
 *       '&:hover': {
 *         backgroundColor: 'primary.main',
 *         opacity: [0.9, 0.8, 0.7],
 *       },
 *     }}
 *   />
 *
 * For inline element styling, use the theme hook
 * @example
 * import { useTheme } from "@mui/material/styles";
 * const { palette } = useTheme();
 *
 * <Element style={{ color: palette.success.dark }} />
 *
 * When using the `styled` api,
 * we have access to the autostoreTheme defined below via theme.palette[option][variant]
 * @example
 * import { styled } from "@mui/material/styles";
 *
 * const CustomizedSlider = styled(Slider)(
 *  ({ theme }) => `
 *    color: ${theme.palette.primary.main};
 *
 *    :hover {
 *      color: ${darken(theme.palette.primary.main, 0.2)};
 *    }
 * );
 */

export const phoneWidth = 600;
export const tabletPortraitWidth = 768;

export const andonColors: { [key: string]: string } = {
  openGreen: "#6dae45",
  handRaisedYellow: "#fdc101",
  portFaultAmber: "#f07b35",
  gridDownPurple: mainColours.mode.goodsIn,
  safetyStopRed: "#bf0001",
  blockerRed: "#db0d1f",
  workstationActiveTeal: "#7e7e7e",
  workstationOpenGreen: "#15844B",
  workstationHandRaisedYellow: "#ffc043",
  portFaultRed: "#E11B22",
  workstationActiveGrey: "grey.600"
};
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    tablet: true; // adds the `tablet` breakpoint
  }
  interface Palette {
    gray: PaletteColor;
    darkGray: PaletteColor;
    statusGray: PaletteColor;
    statusBlue: PaletteColor;
    statusYellow: PaletteColor;
    statusGreen: PaletteColor;
    statusRed: PaletteColor;
    autostoreRed: PaletteColor;
    frozen: PaletteColor;
    chilled: PaletteColor;
    ambient: PaletteColor;
    warning: PaletteColor;
    darkBlue: PaletteColor;
    pickProgressBarGreen: PaletteColor;
    disabled: PaletteColor;
    icon: PaletteColor;
    border: SimplePaletteColorOptions;
    borderPrimary: SimplePaletteColorOptions;
    toastFuseColor: SimplePaletteColorOptions;
    tableRowSelected: SimplePaletteColorOptions;
    toastFuseUnderlay: {
      success: string;
      warning: string;
      info: string;
      error: string;
    };
    productCardSafety: {
      warning: string;
      sanitize: string;
    };
    infoPurple: SimplePaletteColorOptions;
    autostoreDesignSystem: AutostoreDesignSystemColors;
  }

  interface PaletteOptions {
    gray: PaletteColorOptions;
    darkGray: PaletteColorOptions;
    statusGray: PaletteColorOptions;
    statusBlue: PaletteColorOptions;
    statusYellow: PaletteColorOptions;
    statusGreen: PaletteColorOptions;
    statusRed: PaletteColorOptions;
    autostoreRed: PaletteColorOptions;
    frozen: PaletteColorOptions;
    chilled: PaletteColorOptions;
    ambient: PaletteColorOptions;
    darkBlue: PaletteColorOptions;
    pickProgressBarGreen: PaletteColorOptions;
    disabled: PaletteColorOptions;
    icon: PaletteColorOptions;
    border: SimplePaletteColorOptions;
    borderPrimary: SimplePaletteColorOptions;
    toastFuseColor: PaletteColorOptions;
    tableRowSelected?: PaletteColorOptions;
    toastFuseUnderlay?: {
      success: string;
      warning: string;
      info: string;
      error: string;
    };
    productCardSafety?: {
      warning: string;
      sanitize: string;
    };
    infoPurple: PaletteColorOptions;
    autostoreDesignSystem?: AutostoreDesignSystemColors;
  }
}

const autostoreThemeObj: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: phoneWidth,
      tablet: tabletPortraitWidth,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  palette: {
    mode: "light",
    primary: {
      main: mainColours.background.accent.primary,
      dark: mainColours.background.accent.hover,
      light: basicColours.blue["50"]
    },
    secondary: {
      main: mainColours.background.secondary,
      dark: mainColours.background.hover,
      contrastText: mainColours.text.primary
    },
    icon: {
      main: mainColours.icon.primary
    },
    text: {
      primary: mainColours.text.primary,
      secondary: mainColours.text.secondary,
      disabled: mainColours.text.disabled
    },
    border: {
      main: mainColours.border.primary,
      light: mainColours.border.secondary
    },
    borderPrimary: {
      main: mainColours.border.accent
    },
    error: {
      main: mainColours.background.error.primary,
      dark: mainColours.background.error.hover,
      light: basicColours.red[50]
    },
    info: {
      main: mainColours.background.accent.primary,
      dark: mainColours.background.accent.hover,
      light: basicColours.blue[50]
    },
    infoPurple: {
      main: basicColours.purple[100],
      dark: basicColours.purple[700],
      contrastText: mainColours.text.primary
    },
    warning: {
      main: mainColours.background.warning.primary,
      dark: mainColours.background.warning.hover,
      light: basicColours.yellow[100]
    },
    success: {
      main: mainColours.background.success.primary,
      dark: mainColours.background.success.hover,
      light: basicColours.green[50]
    },
    background: {
      default: mainColours.background.primary,
      secondary: mainColours.background.secondary,
      paper: mainColours.background.primary,
      hover: mainColours.background.hover,
      // todo: delete and use a semantic token
      lightBlue: basicColours.blue[50],
      // todo: delete and use a semantic token
      gray: basicColours.mono[50]
    },
    autostoreDesignSystem: autostoreDesignSystemColors,
    // custom colors:
    autostoreRed: {
      main: basicColours.red.main,
      contrastText: mainColours.text.inverse.primary
    },
    gray: {
      light: mainColours.background.secondary,
      main: "#f2f2f2",
      dark: "#e8e8e8"
    },
    darkGray: {
      dark: "#333",
      main: "#757575",
      light: "rgb(202, 202, 202)"
    },
    frozen: {
      main: mainColours.label[3],
      contrastText: basicColours.mono.black
    },
    chilled: {
      main: mainColours.label[1],
      contrastText: basicColours.mono.black
    },
    ambient: {
      main: mainColours.label[7],
      contrastText: basicColours.mono.black
    },
    statusGray: {
      main: mainColours.background.secondary,
      contrastText: basicColours.mono.black
    },
    statusBlue: {
      main: mainColours.label[1],
      contrastText: basicColours.mono.black
    },
    statusRed: {
      main: mainColours.label[6],
      contrastText: basicColours.mono.black
    },
    statusYellow: {
      main: mainColours.label[7],
      contrastText: basicColours.mono.black
    },
    statusGreen: {
      main: mainColours.label[9],
      contrastText: basicColours.mono.black
    },
    darkBlue: { main: "#2260D3" },
    pickProgressBarGreen: { main: "#189A57" },
    disabled: {
      main: "#00000014",
      dark: "#0000004D"
    },
    toastFuseColor: {
      main: mainColours.background.toastFuseColor.primary
    },
    tableRowSelected: {
      main: "#2260D3",
      light: mainColours.background.primary
    },
    toastFuseUnderlay: {
      success: mainColours.background.success.hover,
      warning: mainColours.background.warning.hover,
      info: mainColours.background.accent.hover,
      error: mainColours.background.error.hover
    },
    productCardSafety: {
      warning: mainColours.background.warning.hover,
      sanitize: basicColours.purple[700]
    }
  },
  components: {
    ...muiAlert,
    ...muiAlertTitle,
    ...muiAppBar,
    ...muiButton,
    ...muiButtonGroup,
    ...muiCard,
    ...muiChip,
    ...muiDialogTitle,
    ...muiFormControl,
    ...muiFormLabel,
    ...muiInputBase,
    ...muiMenuItem,
    ...muiOutlinedInput,
    ...muiPaper,
    ...muiSvgIcon,
    ...muiTable,
    ...muiTableCell,
    ...muiTableHead,
    ...muiTableRow,
    ...muiToolbar,
    ...muiSwitch,
    ...muiList
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: 'CircularXX, "Helvetica Neue", Arial, Helvetica, sans-serif',
    tableBody: {
      fontSize: "1.25rem"
    },
    stat: {
      fontSize: "32px",
      fontFamily: 'CircularXX, "Helvetica Neue", Arial, Helvetica, sans-serif'
    },
    button: {
      textTransform: "none"
    }
  }
};

export const tabletWidth = "(max-width: 1200px)";
export const tabletPortrait = `(max-width: ${tabletPortraitWidth}px)`;
export const mobileWidth = `(max-width: ${phoneWidth}px)`;

export const autostoreTheme = createTheme(autostoreThemeObj);
export const styled = createStyled({ defaultTheme: autostoreTheme });
